import { FabricObject, Group, Polygon, XY } from 'fabric';
import { MapObject } from './map-object';
import { Pose, RotationType } from '../../models/pose';
import { Point } from '../../models/interfaces/point';
import { Map } from '../../models/map';
import { RobotModel } from '../../models/robot-model';
import * as _ from 'lodash';

export class InspectFieldObject extends MapObject {
  defaultOptions: any = {
    selectable: false,
    evented: false,
    hoverCursor: 'default',
    opacity: 0.4
  };

  private polygons: Group;

  constructor(public pose: Pose, public map: Map, public robotModel: RobotModel, options?: any) {
    super(options);
  }

  select() {
    this.getFabric().set('opacity', 1);
  }

  deselect() {
    this.getFabric().set('opacity', this.getOptions().opacity);
  }

  update(pose: Pose) {
    this.pose = pose;

    const group: Group = this.getFabric() as Group;
    group.remove(this.polygons);
    group.add(this.buildPolygons());
  }

  protected build(): FabricObject {
    return new Group([this.buildPolygons()], this.getOptions());
  }

  protected buildPolygons() {
    const inspectFieldObjects: Polygon[] = [];
    RobotModel.getCameraFields(this.robotModel).subscribe(cameraFields => {
      _.filter(cameraFields, camfield => camfield.type === 'inspect').forEach(camfield => {
        inspectFieldObjects.push(
          new Polygon(
            this.calculatePoints(camfield.minViewExtent, camfield.maxViewExtent, camfield.minViewWidth, camfield.maxViewWidth, -90),
            this.polygonSettings(camfield.name)));
      });
    });

    this.polygons = new Group(inspectFieldObjects);
    return this.polygons;
  }

  private polygonSettings(camFieldName: string) {
    return {
      fill: this.getCamFieldColor(camFieldName),
      opacity: 0.5
    };
  }

  private getCamFieldColor(camFieldName: string): string {
    let color = 'blue';

    switch (camFieldName) {
      case 'NearField':
        color = 'red';
        break;
      case 'MidField':
        color = 'green';
        break;
      case 'FarField':
        color = 'blue';
        break;
      case 'ProximityFront':
        color = 'blue';
        break;
      case 'ProximityRear':
        color = 'green';
        break;
    }
    return color;
  }

  private calculatePoints(minViewExtent: number, maxViewExtent: number,
                          minViewWidth: number, maxViewWidth: number, angle: number = 0): XY[] {
    const xMax = this.pose.x + maxViewExtent;
    const xMin = this.pose.x + minViewExtent;
    const yMaxLeft = this.pose.y + (maxViewWidth / 2);
    const yMaxRight = this.pose.y - (maxViewWidth / 2);
    const yMinLeft = this.pose.y + (minViewWidth / 2);
    const yMinRight = this.pose.y - (minViewWidth / 2);

    return [[xMax, yMaxLeft], [xMax, yMaxRight], [xMin, yMinRight], [xMin, yMinLeft]].map(coords => {
      const point = this.rotatePoint(coords[0], coords[1], this.pose, this.pose.getRotation(RotationType.Degrees) + angle);
      return this.map.pointToPixels(point);
    });
  }

  private rotatePoint(x: number, y: number, pivot: Point, angle: number): Point {
    const rad = angle * (Math.PI / 180);
    const sin = Math.sin(rad);
    const cos = Math.cos(rad);

    // translate point back to origin:
    const tx = x - pivot.x;
    const ty = y - pivot.y;

    // rotate point
    const xNew = tx * cos - ty * sin;
    const yNew = tx * sin + ty * cos;

    // translate point back:
    return { x: xNew + pivot.x, y: yNew + pivot.y };
  }
}
